import * as React from 'react';
import 'firebase/firestore';
import {
  Card,
  CardContent,
  CircularProgress,
  Typography,
  makeStyles,
} from '@material-ui/core';

import { LocalizerHelper } from '../../helpers/localizerHelper';
import { DINE_IN_TABLE_SITTINGS_MENU_ITEMS_STATUS } from '../../constants/data';
import { formatMenuItemPriceV2 } from '../../helpers/menuItemHelper';
import { useDispatch } from 'react-redux';
import { serializeMenuItemSnap } from '../../features/menu/menu.service';
import { MenuItem } from '../../models/menuItem';
import { firestore } from '../../firebase';
import { setMenuItemPath } from '../../features/menuItem/menuItem.slice';

const useCardStyles = makeStyles(() => ({
  root: {
    boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.15)',
    borderRadius: '16px',
  },
}));

interface MenuItemCardProps {
  menuItemPath: string;
}

const MenuItemCard: React.FC<MenuItemCardProps> = ({
  menuItemPath,
}) => {
  const dispatch = useDispatch();
  const classes = useCardStyles();
  const [menuItem, setMenuItem] = React.useState<MenuItem | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!menuItemPath) return;

    let cancelled = false;

    const fetchMenuItem = async () => {
      try {
        const snap = await firestore.doc(menuItemPath).get();
        if (!cancelled) {
          setMenuItem(serializeMenuItemSnap(snap));
        }
      } catch (err) {
        console.error('Failed to fetch menu item', err);
        if (!cancelled) setMenuItem(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchMenuItem();

    return () => {
      cancelled = true;
    };
  }, [menuItemPath]);

  if (loading) {
    return (
      <Card className={classes.root}>
        <CardContent style={{ textAlign: 'center' }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (!menuItem) return null;

  const isNotAvailable =
    menuItem?.status === DINE_IN_TABLE_SITTINGS_MENU_ITEMS_STATUS.na;

  const headerClassName = isNotAvailable
    ? 'menu-item-header not-available'
    : 'menu-item-header';

  const headerImage =
    menuItem?.media?.[0]?.url && menuItem.media[0].url !== ''
      ? menuItem.media[0].url
      : null;

  return (
    <div key={`menu-item-${menuItem.id}`} className="menu-item">
      <Card
        className={classes.root}
        onClick={() => dispatch(setMenuItemPath(menuItemPath))}
      >
        {headerImage && (
          <div className="header_img_menu_list">
            <img src={headerImage} alt={menuItem.id} />
          </div>
        )}

        <CardContent>
          <div className="row">
            <div className="col-8">
              <Typography
                color="textSecondary"
                gutterBottom
                align="left"
                className={headerClassName}
              >
                {LocalizerHelper.localized(menuItem, ['name'])}
              </Typography>
            </div>

            <div className="col-4 text-right">
              <Typography
                color="textSecondary"
                gutterBottom
                align="right"
                className={`price ${headerClassName}`}
              >
                {isNotAvailable
                  ? 'TERMINATO'
                  : formatMenuItemPriceV2(menuItem)}
              </Typography>
            </div>
          </div>

          <div className="row">
            <div
              className="col-md-12 text-left"
              style={{ whiteSpace: 'pre-line' }}
            >
              {LocalizerHelper.localized(menuItem, ['description'])}
            </div>
          </div>

          <div className="row">
            <div className="col-md-12 text-right">
              {menuItem?.allergens?.map((item: string) => (
                <img
                  key={`allergen-icon-${item}`}
                  src={`/assets/icons/allergens/icon_allergen_${item.toLowerCase()}.png`}
                  className="allergen-icon"
                  alt={item}
                />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MenuItemCard;
